import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

const InlineThreeDViewer = ({ modelUrl, fileName, messageId }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const modelRef = useRef(null);
  const frameRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mouse interaction state
  const mouseState = useRef({
    isDown: false,
    lastX: 0,
    lastY: 0
  });

  const formatLoadError = (err, label = 'model') => {
    const status = err?.target?.status;
    // When CORS blocks XHR, browsers often report status=0 with a ProgressEvent.
    if (status === 0) {
      return `Failed to load ${label}. This is often caused by Storage CORS blocking XHR requests for this origin.`;
    }
    if (typeof status === 'number' && status > 0) {
      return `Failed to load ${label} (HTTP ${status}).`;
    }
    const msg = err?.message || err?.toString?.() || 'Unknown error';
    return `Failed to load ${label}: ${msg}`;
  };

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1A1D21);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, 400 / 300, 0.1, 1000);
    camera.position.z = 5;
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(520, 390); // 30% bigger than original 400x300
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight2.position.set(-10, -10, -5);
    scene.add(directionalLight2);

    mountRef.current.appendChild(renderer.domElement);

    // Load model
    loadModel(modelUrl, scene);

    // Animation loop
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      
      if (modelRef.current) {
        // Simple auto-rotate
        modelRef.current.rotation.y += 0.005;
      }
      
      renderer.render(scene, camera);
    };
    animate();

    // Mouse controls
    const handleMouseDown = (event) => {
      mouseState.current.isDown = true;
      mouseState.current.lastX = event.clientX;
      mouseState.current.lastY = event.clientY;
    };

    const handleMouseMove = (event) => {
      if (!mouseState.current.isDown || !modelRef.current) return;
      
      const deltaX = event.clientX - mouseState.current.lastX;
      const deltaY = event.clientY - mouseState.current.lastY;
      
      modelRef.current.rotation.x += deltaY * 0.01;
      modelRef.current.rotation.y += deltaX * 0.01;
      
      mouseState.current.lastX = event.clientX;
      mouseState.current.lastY = event.clientY;
    };

    const handleMouseLeave = () => {
      mouseState.current.isDown = false; // Stop dragging when mouse leaves
    };

    const handleMouseUp = () => {
      mouseState.current.isDown = false;
    };

    const canvas = renderer.domElement;
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.style.cursor = 'grab';

    // Cleanup
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      // Dispose of Three.js objects
      if (modelRef.current) {
        scene.remove(modelRef.current);
      }
      renderer.dispose();
    };
  }, [modelUrl]);

  const loadModel = async (url, scene) => {
    try {
      setLoading(true);
      setError(null);

      // Handle Firebase Storage URLs with query parameters
      const urlWithoutParams = url.split('?')[0];
      const extension = urlWithoutParams.split('.').pop().toLowerCase();

      if (extension === 'gltf' || extension === 'glb') {
        const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
        const loader = new GLTFLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (gltf) => {
            const model = gltf.scene || gltf;
            setupModel(model, scene);
          },
          undefined,
          (error) => {
            console.error('Error loading GLTF:', error);
            setError(formatLoadError(error, 'GLTF/GLB model'));
            setLoading(false);
          }
        );
      } else if (extension === 'obj') {
        const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
        const loader = new OBJLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (object) => {
            setupModel(object, scene);
          },
          undefined,
          (error) => {
            console.error('Error loading OBJ:', error);
            setError(formatLoadError(error, 'OBJ model'));
            setLoading(false);
          }
        );
      } else if (extension === 'stl') {
        const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
        const loader = new STLLoader();
        loader.setCrossOrigin('anonymous');
        
        loader.load(
          url,
          (geometry) => {
            const material = new THREE.MeshLambertMaterial({ 
              color: 0x3BA6FF,
              side: THREE.DoubleSide
            });
            const mesh = new THREE.Mesh(geometry, material);
            setupModel(mesh, scene);
          },
          undefined,
          (error) => {
            console.error('Error loading STL:', error);
            setError(formatLoadError(error, 'STL model'));
            setLoading(false);
          }
        );
      } else {
        // Fallback
        const geometry = new THREE.BoxGeometry(2, 2, 2);
        const material = new THREE.MeshLambertMaterial({ 
          color: 0x3BA6FF,
          transparent: true,
          opacity: 0.8,
          wireframe: true
        });
        const cube = new THREE.Mesh(geometry, material);
        setupModel(cube, scene);
        setError('Preview not available for this format');
      }
    } catch (err) {
      console.error('Error setting up model loader:', err);
      setError(formatLoadError(err, '3D model viewer'));
      setLoading(false);
    }
  };

  const setupModel = (model, scene) => {
    // Center and scale the model
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Scale model to fit in smaller viewport
    const maxSize = Math.max(size.x, size.y, size.z);
    const scale = 2.5 / maxSize;
    model.scale.multiplyScalar(scale);
    
    // Center the model
    model.position.sub(center.multiplyScalar(scale));
    
    // Add to scene
    scene.add(model);
    modelRef.current = model;
    
    setLoading(false);
  };

  return (
    <div style={{
      marginTop: '0px', // No gap between bubble and viewer
      backgroundColor: '#1A1D21',
      borderRadius: '0px 0px 8px 8px', // Rounded only at bottom to connect to bubble
      border: '1px solid #3C4147',
      borderTop: '1px solid #3BA6FF',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <div ref={mountRef} />
      
      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(26, 29, 33, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#EAEAEA'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '30px', 
              height: '30px', 
              border: '2px solid #3BA6FF', 
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 12px'
            }}></div>
            <p style={{ fontSize: '14px' }}>Loading 3D model...</p>
          </div>
        </div>
      )}
      
      {/* Error overlay */}
      {error && !loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(26, 29, 33, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#9A9FA5',
          padding: '20px'
        }}>
          <div style={{ textAlign: 'center', fontSize: '12px' }}>
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {/* Instructions only */}
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        fontSize: '10px',
        color: 'rgba(234, 234, 234, 0.7)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        padding: '2px 6px',
        borderRadius: '3px'
      }}>
        Drag to rotate
      </div>
    </div>
  );
};

export default InlineThreeDViewer;
